/**
 * Module dependencies.
 */

import React, { createContext, useContext } from 'react';

/**
 * `DrawerContextValue` type.
 */

type DrawerContextValue = {
  // Whether the caller renders inside a drawer body.
  inDrawer: boolean;
  // Register a predicate the drawer calls before closing: returning `true`
  // (unsaved changes) makes the drawer ask for confirmation. Pass `null` to
  // unregister. No-op outside a drawer.
  registerDirtyCheck: (check: (() => boolean) | null) => void;
};

/**
 * `DrawerContext`.
 */

const DrawerContext = createContext<DrawerContextValue>({
  inDrawer: false,
  registerDirtyCheck: () => undefined
});

/**
 * Export `DrawerBodyProvider`.
 */

export function DrawerBodyProvider({
  children,
  registerDirtyCheck
}: {
  children: React.ReactNode;
  registerDirtyCheck: (check: (() => boolean) | null) => void;
}): JSX.Element {
  return <DrawerContext.Provider value={{ inDrawer: true, registerDirtyCheck }}>{children}</DrawerContext.Provider>;
}

/**
 * Export `useDrawer` — the full drawer context.
 */

export function useDrawer(): DrawerContextValue {
  return useContext(DrawerContext);
}

/**
 * Export `useInDrawer` — whether the caller is rendered inside a drawer body.
 */

export function useInDrawer(): boolean {
  return useContext(DrawerContext).inDrawer;
}
